import { useId, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { BeheerderShell } from "@/components/BeheerderShell";
import { Navigatiekaart } from "@/components/Navigatiekaart";
import { Alert } from "@/components/ui/alert";
import { SkeletonLine, SkeletonRegion } from "@/components/ui/skeleton";
import { countLabel } from "@/lib/counts";
import {
  groepLabel,
  navigation,
  paginaLabel,
  paginaOmschrijving,
} from "@/components/navigation";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import {
  aandachtspuntLink,
  useOverzicht,
  type Aandachtspunt,
  type Overzicht,
} from "@/features/overzicht/useOverzicht";

/**
 * Drawn from the same list the sidebar reads, not a second one that happens to agree: a landing
 * page that fell behind the menu would be worse than none, because it would look complete.
 */
export function OverzichtPage() {
  const { t } = useTranslation(["paginas", "navigatie", "common"]);
  const { data: seizoenen } = useSeizoenen();
  // Here, on Ploegen and on Ondersteuners alike, the seizoen last picked on the Seizoenen
  // screen wins over the running one - the three screens ADR-0028 listed as having no way to
  // choose another now all read the same fallback.
  const seizoen = kiesSeizoen(seizoenen);
  const { data: overzicht, isPending } = useOverzicht(seizoen?.id);

  const lines = lineForScreen(t, overzicht, seizoen?.name);

  // Narrowed on the way out, exhaustive on the way in: `COUNTED` is what makes a forgotten key
  // in `lineForScreen` a compile error rather than a card that silently loses its line.
  const line = (to: string) =>
    to in COUNTED ? lines[to as CountedPath] : undefined;

  const cards = navigation.groups.map((group) => (
    <CardGroup key={group.key} label={groepLabel(t, group.key)}>
      {group.items.map((item) => (
        <Navigatiekaart
          key={item.to}
          to={item.to}
          icon={item.icon}
          titel={paginaLabel(t, item.key)}
          omschrijving={paginaOmschrijving(t, item.key)}
          // A bar rather than a placeholder character: the line is the only part of the card
          // still waiting, and text there would collapse and then jump when the answer lands.
          regel={
            line(item.to) ??
            (waits(item.to, isPending) ? <SkeletonLine /> : undefined)
          }
        />
      ))}
    </CardGroup>
  ));

  return (
    <BeheerderShell
      titel={t("navigatie:overzicht.label")}
      uitleg={
        seizoen
          ? `${t("common:seizoen.lopend", { naam: seizoen.name })} ${t("overzicht.uitleg")}`
          : t("overzicht.geenSeizoen")
      }
    >
      <div className="space-y-8">
        <Aandachtspunten
          punten={overzicht?.aandachtspunten}
          geladen={Boolean(overzicht)}
        />

        {/* One region for every bar on the screen, never one per bar. */}
        {isPending ? (
          <SkeletonRegion className="space-y-8">{cards}</SkeletonRegion>
        ) : (
          cards
        )}
      </div>
    </BeheerderShell>
  );
}

/** Whether this card is waiting on the server, as opposed to simply having no line. */
function waits(to: string, isPending: boolean) {
  return isPending && to in COUNTED;
}

/**
 * Three of the eleven carry no count: two have no honest number, and Lokale accounts is counted
 * through Identity in the Api layer, which this page's Application service cannot reach.
 */
const COUNTED = {
  "/ploegen": true,
  "/ondersteuners": true,
  "/seizoenen": true,
  "/wedstrijddagen": true,
  "/inschrijfperiodes": true,
  "/planning": true,
  "/stamgegevens": true,
  "/bevoegdheden": true,
} as const;

type CountedPath = keyof typeof COUNTED;

function lineForScreen(
  t: TFunction<["paginas", "navigatie", "common"]>,
  overzicht: Overzicht | undefined,
  seizoen: string | undefined,
): Record<CountedPath, string | undefined> {
  return {
    "/ploegen": countLabel(overzicht?.ploegen, (count) =>
      t("overzicht.regels.ploegen", { count }),
    ),
    "/ondersteuners": countLabel(overzicht?.ondersteuners, (count) =>
      t("overzicht.regels.ondersteuners", { count }),
    ),
    // A state rather than a count: how many seizoenen exist is not a fact anybody acts on.
    "/seizoenen": seizoen
      ? t("overzicht.regels.seizoen", { naam: seizoen })
      : undefined,
    "/wedstrijddagen": countLabel(overzicht?.wedstrijddagen, (count) =>
      t("overzicht.regels.wedstrijddagen", { count }),
    ),
    "/inschrijfperiodes":
      overzicht === undefined
        ? undefined
        : t("overzicht.regels.inschrijfperiodes", {
            count: overzicht.inschrijfperiodes,
            open: overzicht.openInschrijfperiodes,
          }),
    "/planning": countLabel(overzicht?.indelingen, (count) =>
      t("overzicht.regels.indelingen", { count }),
    ),
    // Its own page counts the four lists; here the card only says how many there are.
    "/stamgegevens": t("overzicht.regels.stamgegevens"),
    "/bevoegdheden": countLabel(overzicht?.bevoegdePersonen, (count) =>
      t("overzicht.regels.bevoegdheden", { count }),
    ),
  };
}

/** `aria-labelledby`, because an unnamed `<section>` is not a region at all. */
function CardGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const id = useId();

  return (
    <section className="space-y-3" aria-labelledby={id}>
      <h2
        id={id}
        className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase"
      >
        {label}
      </h2>
      {/* Three across at most, so a card keeps a readable width - the same grid the
          stamgegevens landing page uses. */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

/**
 * Not the planning's waarschuwingen: those need the engine to have run, while an aandachtspunt
 * is counted off the rows and says the seizoen is not ready. The empty case is drawn, because a
 * panel that disappears is indistinguishable from one that failed to load.
 */
function Aandachtspunten({
  punten,
  geladen,
}: {
  punten: Aandachtspunt[] | undefined;
  geladen: boolean;
}) {
  const { t } = useTranslation("paginas");
  const heading = useId();

  if (!geladen) {
    return null;
  }

  if (!punten || punten.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("overzicht.geenAandachtspunten")}
      </p>
    );
  }

  return (
    <section className="space-y-2" aria-labelledby={heading}>
      <h2
        id={heading}
        className="text-[11px] font-bold tracking-[0.06em] text-muted-foreground uppercase"
      >
        {t("overzicht.aandachtspunten")}
      </h2>

      {punten.map((punt, index) => {
        const to = aandachtspuntLink(punt.code);

        if (!to) {
          return (
            <Alert key={`${punt.code}-${index}`} tone="warning">
              {punt.tekst}
            </Alert>
          );
        }

        return (
          // The whole banner is the target, the way a Navigatiekaart is: an underlined sentence
          // running the width of the notice reads as prose someone has marked up, and a
          // three-line link is a shape nobody aims at. `relative` is what the stretched
          // ::after below measures itself against.
          <Alert
            key={`${punt.code}-${index}`}
            tone="warning"
            className="relative transition-colors hover:border-warning-foreground focus-within:ring-2 focus-within:ring-ring"
          >
            <span className="flex items-center justify-between gap-3">
              {/* The outline moves to the banner through `focus-within`, so a keyboard still lands
                somewhere visible. */}
              <Link
                to={to}
                className="after:absolute after:inset-0 focus-visible:outline-none"
              >
                {punt.tekst}
              </Link>
              <ChevronRight aria-hidden className="size-4 shrink-0" />
            </span>
          </Alert>
        );
      })}
    </section>
  );
}
